import { useQuery } from "@tanstack/react-query";
import { type FC, Suspense, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useNavigate, useParams } from "react-router-dom";
import type { MetaAdsCTA } from "shared";
import {
	MetaAdsAdFormat,
	MetaAdsCampaignApprovalStatus,
	MetaAdsCampaignObjective,
	MetaAdsConversionLocation,
} from "shared";
import { Api } from "../api.config";
import { Card } from "../components/Card";
import { LinkGen } from "../LinkGen";
import { AppPage } from "../layout/AppLayout";
import { CreativeMediaPreview } from "./CreativeMediaPreview";
import {
	metaAdsPreviewFiles,
	metaAdsUseCarouselPreview,
} from "./metaAdsCreative";
import { formatMetaAdsMoney } from "./metaAdsMoney";
import { useRegisterMetaAdsUnsavedWork } from "./metaAdsUnsavedWork";
import { useApproval, useApprovalActions } from "./useApprovals";

const CTA_LABELS: Record<MetaAdsCTA, string> = {
	LEARN_MORE: "Learn More",
	SHOP_NOW: "Shop Now",
	SIGN_UP: "Sign Up",
	CONTACT_US: "Contact Us",
	GET_QUOTE: "Get Quote",
	BOOK_NOW: "Book Now",
	SEND_MESSAGE: "Send Message",
	LIKE_PAGE: "Like Page",
	CALL_NOW: "Call Now",
	BUY_NOW: "Buy Now",
	ORDER_NOW: "Order Now",
};

const OBJECTIVE_LABELS: Record<MetaAdsCampaignObjective, string> = {
	[MetaAdsCampaignObjective.OUTCOME_AWARENESS]: "Awareness",
	[MetaAdsCampaignObjective.OUTCOME_TRAFFIC]: "Traffic",
	[MetaAdsCampaignObjective.OUTCOME_ENGAGEMENT]: "Engagement",
	[MetaAdsCampaignObjective.OUTCOME_LEADS]: "Leads",
	[MetaAdsCampaignObjective.OUTCOME_SALES]: "Sales",
};

const STATUS_BADGE: Record<string, string> = {
	pending: "bg-warning text-dark",
	approved: "bg-success",
	rejected: "bg-danger",
};

const ReviewContent: FC<{ id: number }> = ({ id }) => {
	const navigate = useNavigate();
	const { data: approval } = useApproval(id);
	const { approve, reject } = useApprovalActions();
	const [comment, setComment] = useState("");
	const [showReject, setShowReject] = useState(false);

	const draft = approval.draftSnapshot;
	const isPending = approval.status === MetaAdsCampaignApprovalStatus.PENDING;
	const canReview = approval.canReview;

	useRegisterMetaAdsUnsavedWork(
		isPending && (showReject || comment.trim().length > 0),
		"Unsaved approval review comment",
	);

	const isLeads = draft.objective === MetaAdsCampaignObjective.OUTCOME_LEADS;
	const isLeadsInstantForm =
		isLeads &&
		draft.conversionLocation === MetaAdsConversionLocation.INSTANT_FORM;

	const { data: mediaFiles } = useQuery({
		queryKey: ["MEDIA_BATCH", ...draft.mediaFileIds],
		queryFn: () => Api.GetMediaBatch(draft.mediaFileIds).then((r) => r.data),
		enabled: draft.mediaFileIds.length > 0,
	});

	const handleApprove = async () => {
		await approve.mutateAsync({
			id,
			dto: { comment: comment.trim() || undefined },
		});
		navigate(LinkGen.MetaAds.Approvals(), { replace: true });
	};

	const handleReject = async () => {
		if (!comment.trim()) return;
		await reject.mutateAsync({
			id,
			dto: { comment: comment.trim() },
		});
		navigate(LinkGen.MetaAds.Approvals(), { replace: true });
	};

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
			<Card>
				<Card.Header>
					Submission Info
					<span
						className={`badge ${STATUS_BADGE[approval.status] ?? "bg-secondary"}`}
						style={{ marginLeft: 12 }}
					>
						{approval.status}
					</span>
				</Card.Header>
				<Card.Body>
					<dl style={{ marginBottom: 0 }}>
						<dt>Submitted by</dt>
						<dd>{approval.submittedByName}</dd>
						<dt>Submitted</dt>
						<dd>{new Date(approval.submittedAt).toLocaleString()}</dd>
						{approval.submitterNote && (
							<>
								<dt>Note</dt>
								<dd>
									<em>{approval.submitterNote}</em>
								</dd>
							</>
						)}
						{approval.reviewedByName && (
							<>
								<dt>Reviewed by</dt>
								<dd>{approval.reviewedByName}</dd>
								<dt>Reviewed</dt>
								<dd>
									{approval.reviewedAt
										? new Date(approval.reviewedAt).toLocaleString()
										: "-"}
								</dd>
								{approval.reviewerComment && (
									<>
										<dt>Reviewer comment</dt>
										<dd>
											<em>{approval.reviewerComment}</em>
										</dd>
									</>
								)}
							</>
						)}
					</dl>
				</Card.Body>
			</Card>

			<Card>
				<Card.Header>Campaign Details</Card.Header>
				<Card.Body>
					<dl style={{ marginBottom: 0 }}>
						<dt>Campaign Name</dt>
						<dd>{draft.name}</dd>
						<dt>Objective</dt>
						<dd>{OBJECTIVE_LABELS[draft.objective] ?? draft.objective}</dd>
						<dt>Budget</dt>
						<dd>
							{draft.budgetType} - {formatMetaAdsMoney(draft.budgetAmount)}
						</dd>
						<dt>Start Date</dt>
						<dd>{draft.startDate}</dd>
						{draft.endDate && (
							<>
								<dt>End Date</dt>
								<dd>{draft.endDate}</dd>
							</>
						)}
						<dt>Age Range</dt>
						<dd>
							{draft.ageMin} - {draft.ageMax}
						</dd>
						<dt>Gender</dt>
						<dd>
							{draft.genders.includes(0)
								? "All"
								: draft.genders.includes(1)
									? "Male"
									: "Female"}
						</dd>
						<dt>Optimization</dt>
						<dd>{draft.optimizationGoal}</dd>
						<dt>Bid Strategy</dt>
						<dd>
							{draft.bidStrategy}
							{draft.bidAmount
								? ` (cap: ${formatMetaAdsMoney(draft.bidAmount)})`
								: ""}
						</dd>
					</dl>
				</Card.Body>
			</Card>

			<Card>
				<Card.Header>Creative</Card.Header>
				<Card.Body>
					<dl style={{ marginBottom: 0 }}>
						<dt>Primary Text</dt>
						<dd style={{ whiteSpace: "pre-wrap" }}>{draft.primaryText}</dd>
						<dt>Headline</dt>
						<dd>{draft.headline}</dd>
						<dt>Description</dt>
						<dd>{draft.description}</dd>
						<dt>CTA</dt>
						<dd>{CTA_LABELS[draft.callToAction] ?? draft.callToAction}</dd>
						{draft.destinationUrl && (
							<>
								<dt>Destination URL</dt>
								<dd>{draft.destinationUrl}</dd>
							</>
						)}
					</dl>
					{mediaFiles &&
						mediaFiles.length > 0 &&
						(() => {
							const previewFiles = metaAdsPreviewFiles(
								mediaFiles,
								draft.adFormat,
								draft.mediaFileIds,
							);
							const useCarousel = metaAdsUseCarouselPreview(
								draft.adFormat,
								draft.mediaFileIds,
								previewFiles,
							);
							return (
								<div style={{ marginTop: 12, maxWidth: 400 }}>
									<p className="text-muted mb-2" style={{ fontSize: 14 }}>
										<strong>Format:</strong>{" "}
										{draft.adFormat === MetaAdsAdFormat.CAROUSEL
											? "Carousel (images only)"
											: draft.adFormat === MetaAdsAdFormat.SINGLE_VIDEO
												? "Single Video"
												: "Single Image"}
									</p>
									<CreativeMediaPreview
										files={previewFiles}
										layout={useCarousel ? "carousel" : "grid"}
										aspectRatio="1 / 1"
									/>
								</div>
							);
						})()}
				</Card.Body>
			</Card>

			{isLeadsInstantForm && draft.leadForm && (
				<Card>
					<Card.Header>Lead Form</Card.Header>
					<Card.Body>
						<dl style={{ marginBottom: 0 }}>
							<dt>Form Name</dt>
							<dd>{draft.leadForm.name || "-"}</dd>
							<dt>Questions ({draft.leadForm.questions.length})</dt>
							<dd>
								{draft.leadForm.questions.map((q) => q.label).join(", ") || "-"}
							</dd>
						</dl>
					</Card.Body>
				</Card>
			)}

			{isPending && !canReview && (
				<div className="alert alert-info" role="alert">
					Editors cannot approve campaigns. Only Managers and assigned approvers
					can approve or reject pending submissions.
				</div>
			)}

			{isPending && canReview && (
				<Card>
					<Card.Header>Review Actions</Card.Header>
					<Card.Body>
						<div className="mb-3">
							<label htmlFor="review-comment" className="form-label">
								{showReject
									? "Rejection reason (required)"
									: "Comment (optional)"}
							</label>
							<textarea
								id="review-comment"
								className="form-control"
								rows={3}
								value={comment}
								onChange={(e) => setComment(e.target.value)}
								placeholder={
									showReject
										? "Explain what needs to change..."
										: "Optional comment..."
								}
							/>
						</div>
						<div
							style={{
								display: "flex",
								gap: 12,
							}}
						>
							{!showReject ? (
								<>
									<button
										type="button"
										className="btn btn-success"
										onClick={handleApprove}
										disabled={approve.isPending || reject.isPending}
									>
										{approve.isPending ? "Approving..." : "Approve & Publish"}
									</button>
									<button
										type="button"
										className="btn btn-outline-danger"
										onClick={() => setShowReject(true)}
										disabled={approve.isPending || reject.isPending}
									>
										Reject
									</button>
								</>
							) : (
								<>
									<button
										type="button"
										className="btn btn-danger"
										onClick={handleReject}
										disabled={
											!comment.trim() || approve.isPending || reject.isPending
										}
									>
										{reject.isPending ? "Rejecting..." : "Confirm Rejection"}
									</button>
									<button
										type="button"
										className="btn btn-outline-secondary"
										onClick={() => setShowReject(false)}
										disabled={approve.isPending || reject.isPending}
									>
										Cancel
									</button>
								</>
							)}
						</div>
					</Card.Body>
				</Card>
			)}

			<button
				type="button"
				className="btn btn-outline-secondary"
				style={{ alignSelf: "flex-start" }}
				onClick={() => navigate(LinkGen.MetaAds.Approvals())}
			>
				Back to Approvals
			</button>
		</div>
	);
};

export const PageApprovalReview: FC = () => {
	const { id } = useParams<{ id: string }>();
	const approvalId = Number(id);

	return (
		<AppPage title="Review Campaign Approval">
			<ErrorBoundary
				fallback={
					<Card>
						<Card.Body>
							<p className="text-muted" style={{ marginBottom: 0 }}>
								Unable to load approval details.
							</p>
						</Card.Body>
					</Card>
				}
			>
				<Suspense fallback={<p>Loading approval details...</p>}>
					<ReviewContent id={approvalId} />
				</Suspense>
			</ErrorBoundary>
		</AppPage>
	);
};
